import React, { useCallback, useMemo } from 'react';
import { MdTimeline, MdArrowDropDown, MdArrowDropUp, MdHistory } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import ValueExpressionSelector from '../../valueexpressions/components/ValueExpressionSelector';
import ValueExpression from '../../valueexpressions/models/valueexpressions/ValueExpression';
import PrimaryTimelineChart from '../components/charts/PrimaryTimelineChart';
import { PositiveDirection } from '../../metrics2/models/entities/PositiveDirection';
import formatNumber from 'format-number-with-string';
import { TreeTypeLabels, TreeTypes, TreeType } from '../../metrics2/models/enumerations/TreeType';
import { SidebarViewModes } from '../../app/models/enumerations/SidebarViewMode';
import ExportButton from '../components/ExportButton';
import TinyTimelineChart from '../components/charts/TinyTimelineChart';
import { LocationDetails } from '../components/LocationDetails';
import { DurationModes, Duration } from '@legacy-modules/dashboard/models/enums/Duration';
import { useMultiOrgTree } from '@hooks/use-multi-org-tree-hook';
import { useToggleState } from '@hooks/use-toggle-state-hook';
import { useOverviewExport } from '@other-components/use-overview-export-hook';
import OrgUnit, { OrgUnitPart } from '@legacy-modules/metrics2/models/entities/organization/OrgUnit';
import {
  selectCompareText,
  selectComparisonMode,
  selectPrimaryText,
  selectSidebarSize,
  selectOverviewValueExpression,
  selectPrimaryFilter,
  selectCompareFilter,
  selectValueExpressionHistory,
  selectOverviewWeekdayFilter,
  selectOverviewOrgKey,
  overviewSlice,
} from '@redux';
import { useOverviewContext } from '@contexts/overview-context';
import useMetricQuery from '@hooks/use-metric-query-hook/use-metric-query.hook';
import { useKpiQuery } from '@hooks/use-kpi-query-hook';
import { DurationUtils } from '@utils/duration-utils';

function OverviewStatsContainer() {
  const dispatch = useDispatch();
  const sidebarSize = useSelector(selectSidebarSize);
  const overviewRootOrgKey = useSelector(selectOverviewOrgKey);
  const primaryText = useSelector(selectPrimaryText);
  const compareText = useSelector(selectCompareText);
  const comparisonMode = useSelector(selectComparisonMode);
  const primaryValueExpression = useSelector(selectOverviewValueExpression);
  const primaryFilter = useSelector(selectPrimaryFilter);
  const compareFilter = useSelector(selectCompareFilter);
  const valueExpressionsHistory = useSelector(selectValueExpressionHistory);
  const weekdayFilter = useSelector(selectOverviewWeekdayFilter);
  const { hoverOrgKeyState } = useOverviewContext();
  const [hoverOrgKey] = hoverOrgKeyState;

  const primaryFormatter = primaryValueExpression?.getValueFormatter();

  const [tinyChartsVisible, toggleTinyChartsVisible] = useToggleState(true);

  const primaryOrgKeys = useMemo(() => {
    if (hoverOrgKey) {
      return [hoverOrgKey];
    } else if (primaryFilter?.orgKeys?.length > 0) {
      return primaryFilter.orgKeys;
    }
    return ['country:de'];
  }, [hoverOrgKey, primaryFilter]);

  const properties = useMemo<OrgUnitPart[]>(() => ['properties', 'children'], []);
  const { data: orgData } = useMultiOrgTree({
    orgKeys: primaryOrgKeys,
    parts: properties,
    from: primaryFilter.from,
    to: primaryFilter.to,
  });
  const rootOrgUnits = useMemo(
    () => primaryOrgKeys.map((orgKey) => orgData?.get(orgKey))?.filter((ou) => !!ou),
    [primaryOrgKeys, orgData]
  );
  const orgUnit = rootOrgUnits?.[0];
  const exportData = useOverviewExport(rootOrgUnits);
  const primaryDuration = useMemo(
    () =>
      ({
        from: primaryFilter.from,
        to: primaryFilter.to,
        weekdayFilter,
        mode: DurationModes.Custom,
      } as Duration),
    [primaryFilter, weekdayFilter]
  );

  const primaryDateRange = DurationUtils.getDateRange(primaryDuration);
  const compareDateRange = DurationUtils.getComparisonDateRange(primaryDuration);

  const { data: primaryData } = useKpiQuery(
    {
      orgKeys: primaryOrgKeys,
      expressionsFilter: [{ valueExpression: primaryValueExpression }],
      dateRange: DurationUtils.getDateRange(primaryDuration),
    },
    {
      enabled:
        primaryOrgKeys.length > 0 && !!primaryValueExpression && !!primaryDateRange.from && !!primaryDateRange.to,
    },
    (data) => data.kpis.summary.kpiValues[0]?.value
  );

  const { data: compareData } = useKpiQuery(
    {
      orgKeys: primaryOrgKeys,
      expressionsFilter: [{ valueExpression: primaryValueExpression }],
      dateRange: DurationUtils.getComparisonDateRange(primaryDuration),
    },
    {
      enabled:
        primaryOrgKeys.length > 0 &&
        !!primaryValueExpression &&
        !!compareDateRange.from &&
        !!compareDateRange.to &&
        !!compareFilter,
    },
    (data) => data.kpis.summary.kpiValues[0]?.value
  );

  const [diffClass, formattedDiffValue, formattedDiffPercentage] = useMemo(() => {
    const diffValue = primaryData - compareData;
    const diffClass =
      diffValue > 0 && primaryValueExpression.positiveDirection === PositiveDirection.up
        ? 'positive'
        : diffValue < 0 && primaryValueExpression.positiveDirection === PositiveDirection.down
        ? 'positive'
        : 'negative';
    let formattedDiffValue = primaryFormatter ? primaryFormatter(diffValue) : '';
    if (!formattedDiffValue.startsWith('-')) {
      formattedDiffValue = `+${formattedDiffValue}`;
    }
    let formattedDiffPercentage = '-';
    if (compareData > 0) {
      const diffPercentage = (diffValue / compareData) * 100.0;
      formattedDiffPercentage = formatNumber(diffPercentage || 0, '-#.###.##0,#0%');
      if (!formattedDiffPercentage.startsWith('-')) {
        formattedDiffPercentage = `+${formattedDiffPercentage}`;
      }
    }

    return [diffClass, formattedDiffValue, formattedDiffPercentage];
  }, [primaryData, compareData, primaryValueExpression, primaryFormatter]);

  const multiOrgUnit = !hoverOrgKey && primaryOrgKeys?.length > 1;
  const showParentOrgUnitBtn = multiOrgUnit
    ? false
    : orgUnit?.['orgKey'] === primaryFilter.orgKey &&
      !!orgUnit?.['properties']?.['parentOrgKey'] &&
      orgUnit?.['orgKey'] !== overviewRootOrgKey;

  const orgUnitLabel = multiOrgUnit
    ? 'Mehrere Gebiete'
    : orgUnit
    ? `${getTreeTypeLabel(orgUnit.treeType)} ${(orgUnit.properties as OrgUnit['properties']).name}`
    : 'Deutschland';

  const onValueExpressionSelect = useCallback(
    (valueExpression: ValueExpression) => {
      dispatch(overviewSlice.actions.setPrimaryValueExpression(valueExpression));
    },
    [dispatch]
  );

  return (
    <div className='overview-stats-container' style={{ flexDirection: 'column' }}>
      <div className='head-section'>
        <div className='left'>
          <LocationDetails
            showParentOrgUnitBtn={showParentOrgUnitBtn}
            onUpdateOrgKey={() =>
              dispatch(
                overviewSlice.actions.updateOrgKey({
                  primaryOrgKey: orgUnit?.['properties']?.['parentOrgKey'],
                  compareOrgKey: null,
                  comparisonMode,
                })
              )
            }
            orgUnitLabel={orgUnitLabel}
          />
          <ValueExpressionSelector onValueSelect={onValueExpressionSelect} />
        </div>
        <div className='right'>
          {primaryValueExpression && !compareFilter && (
            <div className='head-values'>
              <div className='value-row primary-value'>
                <span className='value-label'>{primaryText}:</span>
                <span className='value-text'>{primaryFormatter(primaryData)}</span>
              </div>
            </div>
          )}

          {primaryValueExpression && compareFilter && (
            <div className='head-values'>
              <div className='value-row primary-value'>
                <span className='value-label'>{primaryText}</span>
                <span className='value-text'>{primaryFormatter(primaryData)}</span>
              </div>
              <div className='value-row compare-value'>
                <span className='value-label'>{compareText}</span>
                <span className='value-text'>{primaryFormatter(compareData)}</span>
              </div>
              <div className='value-row diff-value'>
                <span className='value-label'>Differenz</span>
                <span className={`value-text ${diffClass}`}>{formattedDiffValue}</span>
              </div>
              <div className='value-row diff-value'>
                <span className='value-label'>&nbsp;</span>
                <span className={`value-text ${diffClass}`}>({formattedDiffPercentage})</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <ExportButton onExport={exportData} />
      <div className='chart-section' id='primary-chart'>
        <h3>
          <span className='section-icon'>
            <MdTimeline />
          </span>
          Zeitlicher Verlauf
        </h3>
        {primaryValueExpression && (
          <div className='primary-chart'>
            <PrimaryTimelineChart />
          </div>
        )}
        {!primaryValueExpression && <div className='no-selection'>Bitte Wert auswählen</div>}
      </div>
      <div className='chart-section' id='tiny-chart-list'>
        <h3>
          <span className='section-icon'>
            <MdHistory />
          </span>
          Zuletzt angesehene Werte
          <span className='toggle' onClick={() => toggleTinyChartsVisible()}>
            {tinyChartsVisible && (
              <span>
                ausblenden <MdArrowDropUp size='26' />
              </span>
            )}
            {!tinyChartsVisible && (
              <span>
                einblenden <MdArrowDropDown size='26' />
              </span>
            )}
          </span>
        </h3>

        <div className={`tiny-chart-list ${!tinyChartsVisible ? 'closed' : ''}`}>
          {valueExpressionsHistory.map((ve: ValueExpression) => {
            return (
              <TinyTimelineChart
                key={`history-tiny-timeline-${ve.identifier}`}
                className='tiny-chart'
                valueExpression={ve}
                tooltips={sidebarSize === SidebarViewModes.extended}
                onClick={() => onValueExpressionSelect(ve)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
export default OverviewStatsContainer;

function getTreeTypeLabel(treeType: TreeType): string {
  if (TreeTypeLabels[treeType] && treeType !== TreeTypes.hermes_org) {
    return TreeTypeLabels[treeType];
  }
  return '';
}
